import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, CheckCheck, RefreshCw, Share2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, EmptyState } from "@/components/common/States";
import { ShareReviewDialog } from "@/components/common/ShareReviewDialog";
import api, { humanError } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

const STATUS_TONE = {
  generating: "bg-[#EEF3FB] text-[#1747B0]",
  ready: "bg-[#E8FBF1] text-[#0B5D3C]",
  partial: "bg-[#FFF7E6] text-[#8A6D00]",
  failed: "bg-[#FFE6E6] text-[#B02328]",
  draft: "bg-[#F6F8FA] text-[#43535E]",
  in_review: "bg-[#FFF7E6] text-[#8A6D00]",
  changes_requested: "bg-[#FFE6E6] text-[#B02328]",
  approved: "bg-[#E8FBF1] text-[#0B5D3C]",
  scheduled: "bg-[#EEF3FB] text-[#1747B0]",
  publishing: "bg-[#EEF3FB] text-[#1747B0]",
  published: "bg-[#E8FBF1] text-[#0B5D3C]",
  failed_pub: "bg-[#FFE6E6] text-[#B02328]",
};

const NET_DOT = {
  instagram: "bg-gradient-to-tr from-[#f58529] via-[#dd2a7b] to-[#8134af]",
  linkedin: "bg-[#0A66C2]",
  facebook: "bg-[#1877F2]",
  x: "bg-black",
};

export default function CampaignDetail() {
  const { id } = useParams();
  const { role } = useAuth();
  const canApprove = can(role, "post.approve");
  const canWrite = can(role, "post.write");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const nav = useNavigate();

  async function load() {
    try { const { data } = await api.get(`/campaigns/${id}`); setData(data); }
    catch (e) { toast.error(humanError(e)); setData({ campaign: null, posts: [] }); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const byDate = useMemo(() => {
    const map = new Map();
    (data?.posts || []).forEach(p => {
      const key = (p.proposed_run_at || p.scheduled_at || p.created_at || "").slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  async function retryFailed() {
    setBusy(true);
    try { const { data } = await api.post(`/campaigns/${id}/retry-failed`); toast.success("Retried failed slots"); load(); }
    catch (e) { toast.error(humanError(e)); }
    finally { setBusy(false); }
  }
  async function bulkApprove() {
    if (!window.confirm("Submit all drafts, then approve them? Posts will move to the Scheduled step next.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${id}/bulk-approve`);
      toast.success(`${data.approved?.length || 0} approved · ${data.skipped?.length || 0} skipped`);
      load();
    } catch (e) { toast.error(humanError(e)); }
    finally { setBusy(false); }
  }

  if (!data) return <div><PageHeader title="Campaign" /><LoadingBlock rows={6} /></div>;
  const c = data.campaign;
  if (!c) return <div><PageHeader title="Campaign" subtitle="Not found" /><Link to="/app/campaigns" className="text-sm font-semibold text-[#1F5FD8] hover:underline">Back to campaigns</Link></div>;

  const posts = data.posts || [];
  const counts = posts.reduce((a, p) => { a[p.status] = (a[p.status] || 0) + 1; return a; }, {});

  return (
    <div>
      <button onClick={() => nav("/app/campaigns")} className="mb-4 inline-flex items-center gap-1 text-sm text-[#43535E] hover:text-[#0B2B33]" data-testid="campaign-back">
        <ChevronLeft className="h-4 w-4" /> All campaigns
      </button>
      <PageHeader
        title={c.goal}
        subtitle={<span>
          {c.start_date} → {c.end_date} · {c.cadence?.replace(/_/g, " ")} ·{" "}
          <span className={`hs-chip ${STATUS_TONE[c.status]}`}>{c.status}</span>
          {c.apply_insights ? <span className="ml-1 hs-chip">insights on</span> : null}
        </span>}
        actions={
          <>
            {canWrite ? (
              <button onClick={() => setShareOpen(true)} data-testid="campaign-share" className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm font-semibold text-[#0B2B33] hover:bg-[#F6F8FA]">
                <Share2 className="mr-1 -mt-0.5 inline h-4 w-4" /> Share for review
              </button>
            ) : null}
            {canWrite && (c.failed_slots || []).length ? (
              <button onClick={retryFailed} disabled={busy} data-testid="campaign-retry-failed" className="rounded-full border border-[#E3252A]/40 bg-[#FFE6E6] px-4 py-2 text-sm font-semibold text-[#B02328] hover:bg-[#FFD4D5]">
                <RefreshCw className="mr-1 -mt-0.5 inline h-4 w-4" /> Retry {c.failed_slots.length} failed
              </button>
            ) : null}
            {canApprove ? (
              <button onClick={bulkApprove} disabled={busy} data-testid="campaign-bulk-approve" className="hs-btn-primary text-sm">
                {busy ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Working…</span> :
                  <><CheckCheck className="mr-1 -mt-0.5 inline h-4 w-4" /> Submit & approve all</>}
              </button>
            ) : null}
          </>
        }
      />

      <ShareReviewDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        scopeKind="campaign"
        scopeId={c.id}
        scopeLabel={c.goal.slice(0, 80)}
      />

      {/* Status strip */}
      <div className="mb-6 grid gap-3 md:grid-cols-6" data-testid="campaign-status-strip">
        {["draft", "in_review", "changes_requested", "approved", "scheduled", "published"].map(s => (
          <div key={s} className="rounded-lg border border-[#E4E9EF] bg-white p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A97A0]">{s.replace(/_/g, " ")}</div>
            <div className="mt-1 font-display text-xl font-bold text-[#0B2B33]">{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      {(c.failed_slots || []).length ? (
        <div className="mb-6 rounded-xl border border-[#FFB4B7] bg-[#FFF5F5] p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#B02328]">
            <AlertCircle className="h-4 w-4" /> {c.failed_slots.length} slots didn't generate
          </div>
          <ul className="grid gap-1 md:grid-cols-3">
            {c.failed_slots.map((s, i) => (
              <li key={i} className="text-xs text-[#43535E]">
                <span className="font-semibold capitalize">{s.network}</span> · {s.date} <span className="text-[#8A97A0]">({s.reason.replace(/_/g, " ")})</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Timeline */}
      {posts.length === 0 ? (
        <EmptyState title="No drafts yet" body={c.status === "generating" ? "Generating — refresh in a moment." : "This campaign didn't produce any drafts."} />
      ) : (
        <div className="rounded-xl border border-[#E4E9EF] bg-white p-5" data-testid="campaign-timeline">
          <div className="mb-3 font-display text-lg font-semibold text-[#0B2B33]">Timeline</div>
          <ol className="relative ml-3 border-l-2 border-[#E4E9EF]">
            {byDate.map(([dateKey, group]) => (
              <li key={dateKey} className="mb-6 pl-5" data-testid={`campaign-day-${dateKey}`}>
                <span className="absolute -left-[9px] mt-1 h-4 w-4 rounded-full border-4 border-white bg-[#1F5FD8]" />
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
                  {dateKey ? new Date(dateKey + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—"}
                </div>
                <ul className="grid gap-2 md:grid-cols-2">
                  {group.map(p => (
                    <li key={p.id}>
                      <Link to={`/app/composer/${p.id}`} className="group block rounded-lg border border-[#E4E9EF] bg-white p-3 hover:border-[#1F5FD8] hover:shadow" data-testid={`campaign-post-${p.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="line-clamp-1 text-sm font-semibold text-[#0B2B33] group-hover:text-[#1F5FD8]">{p.title}</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {(p.variants || []).map(v => (
                                <span key={v.network} className={`inline-flex items-center gap-1 rounded-full bg-[#F6F8FA] px-2 py-0.5 text-[10px] font-semibold`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${NET_DOT[v.network]}`} />
                                  {v.network}
                                </span>
                              ))}
                            </div>
                          </div>
                          <span className={`hs-chip ${STATUS_TONE[p.status] || "bg-[#F6F8FA]"}`}>{p.status.replace(/_/g, " ")}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
